/**
 * Characters that are NOT allowed in an uploaded file's name.
 *
 * This is the exact complement of the server's `SAFE_FILENAME_RE`
 * (`/^[\w. \-()+:]+$/`) in
 * `libs/app/forms/service/feature/.../submit-form-answers.handler.ts`.
 * Keep the two in lock-step: every character this helper emits must pass the
 * server regex, otherwise the form submit is rejected with `unsafe-filename`
 * — and because the stored object keeps the raw name while the server derives
 * the expected path from the sanitised name, it also fails the
 * `pending-url-mismatch` check.
 */
const UNSAFE_FILENAME_CHARS = /[^\w. \-()+:]/g;

/** Quotes/apostrophes that read naturally when simply removed. */
const QUOTE_CHARS = /['"`‘’“”]/g;

/** Combining diacritical marks left behind after NFD normalisation. */
const DIACRITICS = /[\u0300-\u036f]/g;

/**
 * Replaces unsafe characters in an upload filename so it survives the
 * server-side form-submit pipeline instead of failing the submission.
 *
 * Strategy (readable result, always server-safe):
 *  1. Fold accented Latin letters to ASCII (`café` -> `cafe`) instead of
 *     turning them into separators.
 *  2. Drop apostrophes/quotes outright (`o'brien` -> `obrien`).
 *  3. Replace every remaining unsafe character with `-`.
 *  4. Collapse separator/dot runs (the server rejects `..` as path traversal)
 *     and trim stray separators around the name and its extension.
 *  5. Fall back to `file` (prefixing a bare extension) when no usable stem
 *     remains, so the result is always a non-empty, server-safe filename.
 */
export function sanitiseUploadFilename(name: string): string {
  if (!name) return 'file';

  let safe = name
    // 1. Strip diacritics: "café" -> "cafe", "résumé" -> "resume".
    .normalize('NFD')
    .replace(DIACRITICS, '')
    // 2. Drop quotes/apostrophes rather than turning them into separators.
    .replace(QUOTE_CHARS, '')
    // 3. Anything still outside the server's allow-list becomes a dash.
    .replace(UNSAFE_FILENAME_CHARS, '-')
    // 4a. Collapse runs of separators: "a--b" -> "a-b".
    .replace(/[-_]{2,}/g, '-')
    // 4b. Collapse any ".." (or longer) run the server would reject.
    .replace(/\.{2,}/g, '.')
    // 4c. Drop separators hugging a dot: "notes-.pdf" -> "notes.pdf".
    .replace(/[-_]+\.|\.[-_]+/g, '.')
    // 4d. Trim leading/trailing separators and whitespace.
    .replace(/^[-_\s]+|[-_\s]+$/g, '')
    .trim();

  // No usable stem left (empty, all dots, or a bare extension like ".png").
  if (!safe || /^\.+$/.test(safe)) safe = 'file';
  else if (safe.startsWith('.')) safe = 'file' + safe;

  return safe;
}
